import { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const presetPlan = params.get("plan");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      const msg = "É necessário aceitar os termos de tratamento de dados de saúde (LGPD art. 11) para criar a conta.";
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true); setError("");
    try {
      await register(form);
      toast.success("Conta criada com sucesso.");
      const redirectTo = location?.state?.redirectTo;
      const openBooking = location?.state?.openBooking;
      if (redirectTo) {
        navigate(redirectTo, { state: openBooking ? { openBooking: true } : undefined });
      } else {
        navigate(presetPlan ? `/app/assinatura?plan=${presetPlan}` : "/app");
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30" />
        <div className="relative h-full p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/" className="flex items-center gap-3" data-testid="register-brand-link">
            <div className="w-10 h-10 rounded-sm bg-primary-foreground text-primary grid place-items-center">
              <Leaf className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="brand-wordmark font-bold text-base uppercase">Centro de Performance</div>
              <div className="text-xs text-gold opacity-90 overline tracking-[0.22em]">Farmacêutica</div>
            </div>
          </Link>
          <div>
            <div className="overline opacity-80 mb-4">Cadastro</div>
            <h2 className="font-heading text-4xl font-bold leading-tight">
              Comece seu<br />acompanhamento<br />clínico hoje.
            </h2>
          </div>
          <p className="text-sm opacity-80 max-w-sm">
            Após o cadastro você escolhe seu plano e libera o primeiro protocolo.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <div className="overline text-muted-foreground mb-2">Criar conta</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Vamos começar</h1>
          </div>

          <div className="space-y-4">
            <div>
              <label className="overline text-muted-foreground mb-2 block">Nome completo</label>
              <input
                type="text" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-2 block">Email</label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-2 block">Telefone (opcional)</label>
              <input
                type="tel" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="+55 11 99999-0000"
                data-testid="register-phone-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-2 block">Senha (mín. 6)</label>
              <input
                type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="register-password-input"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground leading-snug cursor-pointer" data-testid="register-consent-wrap">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5 accent-primary w-4 h-4"
                data-testid="register-consent-checkbox"
              />
              <span>
                Estou de acordo com o tratamento dos meus <b>dados sensíveis de saúde</b> pela clínica
                (LGPD art. 11), exclusivamente para acompanhamento clínico. Posso solicitar exclusão
                total dos meus dados a qualquer momento.
              </span>
            </label>
          </div>

          {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}

          <button
            type="submit" disabled={loading}
            className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            data-testid="register-submit-btn"
          >
            {loading ? "Criando…" : "Criar minha conta"}
          </button>

          <div className="text-sm text-muted-foreground text-center">
            Já tem conta? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-to-login">Entrar</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
